import type { ReactNode } from 'react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { getBooks } from '@/api/books';
import type { ListBooksResponse, Placement } from '@/api/types';
import { BookCover } from '@/components/BookCover/BookCover';
import { ListPagination } from '@/components/ListPagination/ListPagination';
import styles from './SeriesPage.module.css';

const PAGE_SIZE = 24;

export function SeriesPage(): ReactNode {
  const { seriesId } = useParams<{ seriesId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [listData, setListData] = useState<ListBooksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const seriesNameFromQuery = searchParams.get('name') ?? '';
  const placementParam = searchParams.get('placement') ?? '';
  const placementFilter: Placement | undefined =
    placementParam === 'collection' || placementParam === 'wishlist' || placementParam === 'in_transit'
      ? (placementParam as Placement)
      : undefined;

  const page = useMemo(
    () => Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1),
    [searchParams],
  );

  const buildSearchString = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      const s = next.toString();
      return s ? `?${s}` : '';
    },
    [searchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      if (!seriesId) return;
      const search = buildSearchString((next) => {
        if (p <= 1) next.delete('page');
        else next.set('page', String(p));
      });
      navigate({ pathname: `/collections/${encodeURIComponent(seriesId)}`, search });
    },
    [seriesId, navigate, buildSearchString],
  );

  const load = useCallback(() => {
    if (!seriesId) return;
    setLoading(true);
    setError(null);
    getBooks({
      seriesId,
      page,
      limit: PAGE_SIZE,
      sortBy: 'part',
      sortOrder: 'asc',
      placement: placementFilter,
    })
      .then((res) => {
        setListData(res);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки книг цикла');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [seriesId, page, placementFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const placementStats = useMemo((): Record<Placement, number> => {
    const s = listData?.stats.byPlacement;
    return (
      s ?? {
        collection: 0,
        wishlist: 0,
        in_transit: 0,
      }
    );
  }, [listData]);

  useEffect(() => {
    if (!listData || listData.total === 0 || !seriesId) return;
    const last = Math.max(1, Math.ceil(listData.total / PAGE_SIZE));
    if (page > last) {
      const search = buildSearchString((next) => {
        if (last <= 1) next.delete('page');
        else next.set('page', String(last));
      });
      navigate({ pathname: `/collections/${encodeURIComponent(seriesId)}`, search }, { replace: true });
    }
  }, [listData, page, seriesId, navigate, buildSearchString]);

  const handleBack = () => {
    navigate('/collections');
  };

  if (!seriesId) {
    return (
      <Box className={styles.root}>
        <Typography color="error">Идентификатор цикла не указан.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={styles.root}>
        <Box className={styles.header}>
          <Button
            variant="outlined"
            size="small"
            className={styles.navButton}
            onClick={handleBack}
          >
            ← Коллекции
          </Button>
        </Box>
        <Box className={styles.center}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.root}>
        <Box className={styles.header}>
          <Button
            variant="outlined"
            size="small"
            className={styles.navButton}
            onClick={handleBack}
          >
            ← Коллекции
          </Button>
        </Box>
        <Typography color="error" className={styles.message}>
          {error}
        </Typography>
      </Box>
    );
  }

  const items = listData?.items ?? [];
  const total = listData?.total ?? 0;

  const seriesName =
    seriesNameFromQuery || items[0]?.series?.name || 'Цикл';

  const handlePlacementClick = (placement: Placement) => {
    const next = new URLSearchParams(searchParams);
    next.delete('page');
    if (placementFilter === placement) {
      next.delete('placement');
    } else {
      next.set('placement', placement);
    }
    const s = next.toString();
    navigate({
      pathname: `/collections/${encodeURIComponent(seriesId)}`,
      search: s ? `?${s}` : '',
    });
  };

  return (
    <Box className={styles.root}>
      <Box className={styles.header}>
        <Box>
          <Button
            variant="outlined"
            size="small"
            className={styles.navButton}
            onClick={handleBack}
          >
            ← Коллекции
          </Button>
          <Typography variant="h5" component="h1" className={styles.title}>
            {seriesName}
          </Typography>
          {(placementStats.collection > 0 ||
            placementStats.wishlist > 0 ||
            placementStats.in_transit > 0) && (
            <>
              <Typography variant="body2" color="text.secondary">
                {total} {total === 1 ? 'книга' : 'книг'}
                {placementFilter ? ' с выбранным фильтром' : ' в цикле'}
              </Typography>
              <Box className={styles.chips}>
                <PlacementChip
                  placement="collection"
                  count={placementStats.collection}
                  label="В коллекции"
                  color="success"
                  selected={placementFilter === 'collection'}
                  onClick={() => handlePlacementClick('collection')}
                />
                <PlacementChip
                  placement="wishlist"
                  count={placementStats.wishlist}
                  label="В вишлисте"
                  color="warning"
                  selected={placementFilter === 'wishlist'}
                  onClick={() => handlePlacementClick('wishlist')}
                />
                <PlacementChip
                  placement="in_transit"
                  count={placementStats.in_transit}
                  label="В пути"
                  color="info"
                  selected={placementFilter === 'in_transit'}
                  onClick={() => handlePlacementClick('in_transit')}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {items.length === 0 ? (
        <Typography color="text.secondary" className={styles.message}>
          {placementFilter
            ? 'Нет книг с выбранным размещением.'
            : 'В этом цикле пока нет книг.'}
        </Typography>
      ) : (
        <Box component="ul" className={styles.list}>
          {items.map((book) => (
            <Card key={book.id} component="li" className={styles.card}>
              <Link to={`/books/${book.id}/edit`} className={styles.cardLink}>
                <CardContent className={styles.cardContent}>
                  <Box className={styles.cardInner}>
                    <BookCover
                      coverUrl={book.coverUrl}
                      title={book.title}
                      className={styles.cover}
                    />
                    <Box className={styles.cardMeta}>
                      <Typography variant="subtitle2" className={styles.bookTitle}>
                        {book.part != null ? `Часть ${book.part}. ${book.title}` : book.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {book.author.name}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Link>
            </Card>
          ))}
        </Box>
      )}
      <ListPagination
        page={page}
        pageCount={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        total={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </Box>
  );
}

interface PlacementChipProps {
  placement: Placement;
  count: number;
  label: string;
  color: 'success' | 'warning' | 'info';
  selected?: boolean;
  onClick?: () => void;
}

function PlacementChip({ count, label, color, selected, onClick }: PlacementChipProps): ReactNode {
  if (!count) return null;
  return (
    <Chip
      size="small"
      label={`${label}: ${count}`}
      color={color}
      variant={selected ? 'filled' : 'outlined'}
      onClick={onClick}
      clickable={!!onClick}
    />
  );
}

